import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import ThemeToggle from "@/components/ThemeToggle";
import BrandLogo from "@/components/BrandLogo";
import {
  Brain, BookOpen, Sparkles, Layers, Smartphone, ShieldCheck, Mail, Phone, Facebook, MapPin, ArrowRight, CheckCircle2, GraduationCap,
  Trophy, Award, Flame, Radio, Headphones, WifiOff, Lock, Bell, Share2, BarChart3, Bus, Download, Users, RefreshCw, Apple, Play, Repeat, Calculator,
} from "lucide-react";

export default function Landing() {
  const [landing, setLanding] = useState(null);
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    axios.get(`${API}/landing`).then((r) => {
      setLanding(r.data.landing);
      setCourses(r.data.courses || []);
    }).catch(() => {});
  }, []);

  const lp = landing || {};

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-sans transition-colors">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center" data-testid="landing-logo">
            <BrandLogo size="md" className="sm:h-10" />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600 dark:text-slate-300">
            <a href="#features" className="hover:text-slate-900 dark:hover:text-white">Features</a>
            <a href="#how" className="hover:text-slate-900 dark:hover:text-white">How It Works</a>
            <a href="#courses" className="hover:text-slate-900 dark:hover:text-white">Courses</a>
            <a href="#contact" className="hover:text-slate-900 dark:hover:text-white">Contact</a>
          </nav>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <ThemeToggle data-testid="landing-theme-toggle" />
            <Link to="/login"><Button variant="ghost" data-testid="landing-login-btn">Login</Button></Link>
            <Link to="/register"><Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="landing-register-btn">Register</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 stagger">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-6">
            <Sparkles className="w-3.5 h-3.5" /> AI-Assisted Review Platform
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.02] tracking-tight">
            {lp.hero_title || "Pass Your Board & Civil Service Exam — with AI on Your Side"}
          </h1>
          <p className="mt-6 text-lg text-slate-600 dark:text-slate-300 max-w-2xl leading-relaxed">
            {lp.hero_subtitle || "PrepMaster combines structured Philippine board-exam review with the Civil Service Examination (CSE) — powered by AI explanations, smart spaced-repetition, gamified streaks, and an offline-ready PWA built for the daily commute."}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/register">
              <Button size="lg" className="bg-[#1E3A8A] hover:bg-[#1E40AF] h-12 px-6 text-base" data-testid="hero-get-started-btn">
                Get Started Free <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" className="h-12 px-6 text-base border-slate-300" data-testid="hero-login-btn">
                Login
              </Button>
            </Link>
          </div>
          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-600 dark:text-slate-300">
            <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-600" /> AI rationales on every item</span>
            <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-600" /> Works offline (PWA)</span>
            <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-600" /> Board & Civil Service ready</span>
          </div>
        </div>
        <div className="lg:col-span-5 relative">
          <div className="absolute -inset-4 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-3xl blur-2xl opacity-60" />
          <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-xl">
            <picture>
              <source srcSet="/landing-hero.webp" type="image/webp" />
              <img
                src="/landing-hero.jpg"
                alt="Filipino student preparing for board and civil service exams with AI-assisted review"
                width="1200"
                height="715"
                fetchpriority="high"
                decoding="async"
                className="w-full h-[460px] object-cover"
              />
            </picture>
            <div className="absolute top-4 left-4 bg-white/95 backdrop-blur rounded-lg shadow-lg p-3 flex items-center gap-2 text-xs font-semibold text-slate-900">
              <Brain className="w-4 h-4 text-indigo-600" /> AI Explanation Generated
            </div>
            <div className="absolute bottom-4 right-4 bg-[#1E3A8A] text-white rounded-lg shadow-lg p-3 text-xs font-semibold flex items-center gap-2">
              <Flame className="w-4 h-4 text-orange-300" /> 7-Day Streak
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="bg-white dark:bg-slate-900 border-y border-slate-200 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-3">Why Choose PrepMaster</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Built for serious exam preparation — board exams and civil service alike</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Brain, title: "AI Explanations", body: "Every question comes with an AI-generated rationale that explains why each answer is right — or wrong." },
              { icon: Sparkles, title: "Smart Review (FSRS)", body: "A modern spaced-repetition engine surfaces your weakest topics and chronically-missed items first, so every minute of study pays off." },
              { icon: Headphones, title: "Podcast Mode", body: "Listen to a whole module like a podcast with adjustable pauses after the question, choices, and answer — perfect for hands-free commute review." },
              { icon: BarChart3, title: "Personal Analytics", body: "See accuracy trends, mastery distribution, weakest subjects, and an AI Learning Insight you can act on this week." },
              { icon: Trophy, title: "Gamified Learning", body: "Unlockable badges, daily streaks, leaderboards, and a shareable Progress Card to keep you accountable." },
              { icon: WifiOff, title: "Offline-First PWA", body: "Cache a Smart-Review set and practice on the MRT, jeepney, or anywhere with no Wi-Fi — answers sync when you reconnect." },
              { icon: Bell, title: "Push Notifications", body: "Daily streak reminders, badge unlocks, and broadcasts from your reviewers — straight to your phone via Firebase Cloud Messaging." },
              { icon: Users, title: "Social &amp; Friends", body: "Find study buddies, follow classmates, and celebrate wins together with an in-app feed built for reviewers." },
              { icon: Repeat, title: "Multi-Course Access", body: "One account, many exams. Enrol in Board Exam tracks and the Civil Service Examination side by side — pay only for what you need." },
              { icon: Apple, title: "In-App Purchases", body: "Buy course access instantly with Apple Pay or Google Play — no forms, no waiting for admin verification." },
              { icon: Lock, title: "Secure by Default", body: "One-device-at-a-time login, Cloudflare bot protection, email verification, and a per-student security log keep your account safe." },
              { icon: Smartphone, title: "Native iOS &amp; Android Apps", body: "The full experience packaged as native mobile apps — with push, background audio, and store-verified purchases." },
            ].map((f, i) => (
              <div key={f.title} className="p-6 border border-slate-200 rounded-md hover:-translate-y-1 hover:shadow-lg transition-all duration-300 bg-white" data-testid={`feature-card-${i}`}>
                <div className="h-10 w-10 rounded-md bg-blue-50 grid place-items-center text-blue-700 mb-4">
                  <f.icon className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-lg font-bold">{f.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed" dangerouslySetInnerHTML={{ __html: f.body }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how" className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-3">How It Works</div>
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl">From sign-up to passing day — in six steps</h2>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { t: "Create your account", b: "Free registration. Choose your exam track (Board Exam or Civil Service)." },
            { t: "Get admin approval", b: "Verified students are activated within hours so content stays trusted." },
            { t: "Pick your course", b: "Browse approved licensure programs or the CSE Professional / Sub-Professional level." },
            { t: "Practice with AI feedback", b: "Read-aloud, AI rationales, keyword highlights — built for active recall." },
            { t: "Let Smart Review adapt", b: "Spaced repetition focuses on your weak items so retention compounds." },
            { t: "Track, share, and pass", b: "Streaks, badges, analytics, and a shareable Progress Card keep you motivated." },
          ].map((s, i) => (
            <div key={s.t} className="border border-slate-200 rounded-md p-6 bg-white" data-testid={`step-card-${i}`}>
              <div className="text-xs font-bold text-blue-600">STEP 0{i + 1}</div>
              <div className="font-display text-xl font-bold mt-2">{s.t}</div>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features deep-dive */}
      <section id="features" className="bg-white dark:bg-slate-900 border-y border-slate-200 py-24">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-3">Inside the App</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">More than a quiz app — a complete review system</h2>
            <ul className="mt-8 space-y-4">
              {[
                { icon: Brain, t: "AI-generated rationales for every question" },
                { icon: Sparkles, t: "Adaptive FSRS spaced-repetition with mastery tracking" },
                { icon: Headphones, t: "Podcast Mode with per-section pauses (question · choices · answer)" },
                { icon: Bus, t: "Hands-free Commute Mode with lock-screen controls" },
                { icon: Award, t: "Unlockable achievement badges" },
                { icon: Flame, t: "Daily streak tracker with offline grace" },
                { icon: Trophy, t: "Real-time student leaderboards" },
                { icon: WifiOff, t: "Full offline practice via PWA caching" },
                { icon: Bell, t: "Push notifications for streaks &amp; badges" },
                { icon: Users, t: "Friends graph, social feed, and study buddies" },
                { icon: Share2, t: "Shareable Progress Card (public link)" },
                { icon: Apple, t: "Native iOS + Android apps with App Store &amp; Play Billing" },
                { icon: RefreshCw, t: "Multi-course access — one account, many exams" },
                { icon: BarChart3, t: "Per-subject accuracy &amp; trend charts" },
                { icon: Download, t: "Export your learning report as CSV" },
              ].map((t) => (
                <li key={t.t} className="flex items-start gap-3 text-slate-700">
                  <t.icon className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" strokeWidth={1.75} />
                  <span dangerouslySetInnerHTML={{ __html: t.t }} />
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7">
            <Card className="p-6 border-slate-200 shadow-sm">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-3">Sample Question</div>
              <p className="text-lg text-slate-800 dark:text-slate-100 leading-relaxed">
                Which of the following is the <span className="keyword-highlight">primary purpose</span> of formative assessment?
              </p>
              <div className="mt-4 grid gap-2 text-sm">
                {[
                  "A. To assign final grades",
                  "B. To monitor student learning and provide feedback",
                  "C. To rank students",
                  "D. To eliminate underperformers",
                ].map((c, i) => (
                  <div key={c} className={`p-3 rounded-md border ${i === 1 ? "bg-green-50 border-green-200 dark:text-emerald-200" : "border-slate-200"}`}>{c}</div>
                ))}
              </div>
              <div className="ai-card mt-4 p-4">
                <div className="text-xs font-bold text-indigo-700 mb-1">AI EXPLANATION</div>
                <p className="text-sm text-slate-700">
                  Formative assessment is used during the learning process to monitor student progress and provide timely feedback — the foundation for improving instruction.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Exam Tracks */}
      <section id="courses" className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-3">Exam Tracks We Cover</div>
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight max-w-3xl">From licensure boards to the Civil Service Examination — one platform for every Filipino professional</h2>

        {/* Two-column track callout — admin-editable via
            Settings → Landing Page → Exam Tracks. Falls back to the
            built-in Board Exam + Civil Service cards when no admin
            tracks have been saved yet. */}
        {(() => {
          const TRACK_THEMES = {
            blue:    { badge: "text-blue-700",  wrap: "border-slate-200 from-blue-50 to-indigo-50",   check: "text-blue-700" },
            amber:   { badge: "text-amber-800", wrap: "border-amber-200 from-amber-50 to-orange-50", check: "text-amber-800" },
            emerald: { badge: "text-emerald-700", wrap: "border-emerald-200 from-emerald-50 to-teal-50", check: "text-emerald-700" },
            violet:  { badge: "text-violet-700",  wrap: "border-violet-200 from-violet-50 to-fuchsia-50", check: "text-violet-700" },
          };
          const defaultTracks = [
            {
              badge_label: "Board Exam Track",
              title: "Philippine Licensure Examinations",
              subtitle: "Structured review for PRC-administered board exams across multiple professions — built around the Course → Subject → Module hierarchy and real exam coverage.",
              items: [
                "Licensure Examination for Teachers (LET)",
                "Nursing Licensure Examination (NLE)",
                "Certified Public Accountant (CPALE)",
                "Criminologist Licensure Examination (CLE)",
                "Marine Deck & Engine Officer exams",
                "Civil, Mechanical, & Electrical Engineering boards",
              ],
              color_theme: "blue",
            },
            {
              badge_label: "Civil Service Track — NEW",
              title: "Civil Service Examination (CSE)",
              subtitle: "Comprehensive CSC-CSE prep — both Professional and Sub-Professional levels — including English & Filipino vocabulary, analogies, paragraph reading, numerical reasoning, and clerical aptitude.",
              items: [
                "CSE — Professional Level",
                "CSE — Sub-Professional Level",
                "Verbal Reasoning (English & Filipino)",
                "Numerical Reasoning & Problem Solving",
                "Analytical Ability & Clerical Operations",
                "General Information (Philippine Constitution, R.A. 6713, etc.)",
              ],
              color_theme: "amber",
            },
          ];
          const tracks = (Array.isArray(landing?.landing_tracks) && landing.landing_tracks.length > 0)
            ? landing.landing_tracks
            : defaultTracks;
          return (
            <div className="mt-10 grid md:grid-cols-2 gap-6">
              {tracks.map((t, i) => {
                const theme = TRACK_THEMES[t.color_theme] || TRACK_THEMES.blue;
                return (
                  <div key={i} className={`p-7 rounded-xl border bg-gradient-to-br ${theme.wrap}`} data-testid={`track-${i}`}>
                    {t.badge_label && (
                      <div className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] ${theme.badge}`}>
                        <GraduationCap className="w-4 h-4" /> {t.badge_label}
                      </div>
                    )}
                    {t.title && <h3 className="font-display text-2xl font-bold mt-2">{t.title}</h3>}
                    {t.subtitle && <p className="text-sm text-slate-700 mt-2">{t.subtitle}</p>}
                    {Array.isArray(t.items) && t.items.length > 0 && (
                      <ul className="mt-4 space-y-1.5 text-sm text-slate-700">
                        {t.items.map((it, j) => (
                          <li key={j} className="flex gap-2">
                            <CheckCircle2 className={`w-4 h-4 ${theme.check} mt-0.5`} /> {it}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Live courses from API + admin-authored promotional cards.
            Hidden when admin has explicitly unpublished every course and
            added no extra cards from Settings → Landing Page. */}
        {landing && (courses.length > 0 || (Array.isArray(landing?.landing_extra_courses) && landing.landing_extra_courses.length > 0)) && (
        <div className="mt-12">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-3">Available Courses</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((c, i) => (
              <div key={c.id} className="p-6 border border-slate-200 rounded-md bg-white hover:-translate-y-1 hover:shadow-lg transition-all duration-300" data-testid={`course-card-${i}`}>
                <div className="h-10 w-10 rounded-md bg-blue-50 grid place-items-center text-blue-700 mb-4">
                  <GraduationCap className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-lg font-bold">{c.course_name}</h3>
                <p className="text-sm text-slate-600 mt-2">{c.description}</p>
              </div>
            ))}
            {(landing?.landing_extra_courses || []).map((c, i) => (
              <div key={`extra-${i}`} className="p-6 border border-slate-200 rounded-md bg-white hover:-translate-y-1 hover:shadow-lg transition-all duration-300" data-testid={`extra-course-card-${i}`}>
                <div className="h-10 w-10 rounded-md bg-amber-50 grid place-items-center text-amber-700 mb-4">
                  <GraduationCap className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-lg font-bold">{c.title}</h3>
                {c.description && <p className="text-sm text-slate-600 mt-2">{c.description}</p>}
              </div>
            ))}
          </div>
        </div>
        )}
      </section>

      {/* Trust */}
      <section className="bg-[#1E3A8A] text-white py-20">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-8 text-center">
          {[
            { t: "Built for high-stakes exams", b: "Focused content for PRC board exams and the Civil Service Examination." },
            { t: "Adaptive review system", b: "Smart Review + Course→Subject→Module hierarchy keeps you on track." },
            { t: "Works where you study", b: "Offline-first PWA, audio mode, push notifications, and night-mode reading." },
          ].map((x, i) => (
            <div key={x.t} data-testid={`trust-card-${i}`}>
              <h3 className="font-display text-2xl font-bold">{x.t}</h3>
              <p className="mt-3 text-white/80">{x.b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-3">Contact</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Get in touch with the PrepMaster team</h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">We're here to help students and institutions get the most out of board and civil-service exam preparation.</p>
          </div>
          <div className="space-y-4 text-slate-700 dark:text-slate-200">
            <div className="flex items-center gap-3"><Mail className="w-5 h-5 text-blue-600" /><span data-testid="contact-email">{lp.contact_email}</span></div>
            <div className="flex items-center gap-3"><Phone className="w-5 h-5 text-blue-600" /><span data-testid="contact-phone">{lp.contact_number}</span></div>
            <div className="flex items-center gap-3"><Facebook className="w-5 h-5 text-blue-600" /><a href={lp.facebook_link} className="hover:underline">Facebook</a></div>
            <div className="flex items-center gap-3"><MapPin className="w-5 h-5 text-blue-600" /><span>{lp.address}</span></div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-50 dark:bg-slate-900 border-t border-slate-200 py-10">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-6 text-sm text-slate-600 dark:text-slate-300">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <BrandLogo size="sm" />
            </div>
            <p>{lp.tagline || "AI-Assisted Board Exam & Civil Service Review Platform — built for Filipino learners."}</p>
          </div>
          <div>
            <div className="font-semibold text-slate-900 dark:text-slate-100 mb-2">Legal</div>
            <ul className="space-y-1.5 text-sm">
              <li>
                <Link
                  to="/legal/privacy"
                  className="text-slate-600 dark:text-slate-300 hover:text-[#1E3A8A] dark:hover:text-blue-300 hover:underline"
                  data-testid="footer-privacy-link"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/legal/terms"
                  className="text-slate-600 dark:text-slate-300 hover:text-[#1E3A8A] dark:hover:text-blue-300 hover:underline"
                  data-testid="footer-terms-link"
                >
                  Terms of Use
                </Link>
              </li>
              <li>
                <Link
                  to="/support"
                  className="text-slate-600 dark:text-slate-300 hover:text-[#1E3A8A] dark:hover:text-blue-300 hover:underline"
                  data-testid="footer-support-link"
                >
                  Help &amp; Support
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <div className="font-semibold text-slate-900 dark:text-slate-100 mb-2">Contact</div>
            <p>{lp.contact_email}</p>
            <p>{lp.contact_number}</p>
            <p className="mt-3 text-xs">{lp.footer_text}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
